package com.karyadina.ui.screens.flashcards

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.karyadina.model.FlashcardStats
import com.karyadina.model.SubjectStats
import com.karyadina.ui.screens.flashcards.components.*
import com.karyadina.ui.theme.FlashcardColors

@Composable
fun StatsDetailScreen(
    stats: FlashcardStats,
    subjectStats: List<Pair<String, SubjectStats>>,
    onBackClick: () -> Unit,
    viewModel: FlashcardViewModel? = null,
    paddingValues: PaddingValues = PaddingValues(0.dp)
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(paddingValues)
            .padding(24.dp)
            .verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(32.dp)
    ) {
            // Section 1: Circular Progress - Main mastery percentage
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(
                    containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.4f)
                ),
                shape = androidx.compose.foundation.shape.RoundedCornerShape(20.dp)
            ) {
                val masteryPercentage = if (stats.totalCards > 0) {
                    (stats.masteredCards.toFloat() / stats.totalCards) * 100
                } else {
                    0f
                }

                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(24.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    CircularProgressGraph(
                        percentage = masteryPercentage,
                        size = 180f,
                        strokeWidth = 10f
                    )
                }
            }

            // Section 2: Mastery Distribution (Stats Grid)
            Column(
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Text(
                    "Overview",
                    style = MaterialTheme.typography.titleLarge.copy(
                        fontWeight = FontWeight.Bold
                    )
                )

                StatsGridGraph(
                    items = listOf(
                        StatItem(
                            label = "Total Cards",
                            value = "${stats.totalCards}",
                            color = MaterialTheme.colorScheme.primary
                        ),
                        StatItem(
                            label = "Mastered",
                            value = "${stats.masteredCards}",
                            color = FlashcardColors.getCorrectAnswerColor()
                        ),
                        StatItem(
                            label = "In Review",
                            value = "${(stats.totalCards - stats.masteredCards - stats.cardsToReview).coerceAtLeast(0)}",
                            color = Color(0xFFFFC107)
                        ),
                        StatItem(
                            label = "Due Today",
                            value = "${stats.cardsToReview}",
                            color = MaterialTheme.colorScheme.error
                        )
                    )
                )
            }

            // Section 3: Subject Comparison (Bar Chart)
            if (subjectStats.isNotEmpty()) {
                Column(
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        "By Subject",
                        style = MaterialTheme.typography.titleLarge.copy(
                            fontWeight = FontWeight.Bold
                        )
                    )

                    BarChartGraph(
                        items = subjectStats.map { (subjectName, subjectStats) ->
                            val percentage = if (subjectStats.totalCards > 0) {
                                (subjectStats.masteredCards.toFloat() / subjectStats.totalCards) * 100
                            } else {
                                0f
                            }
                            BarChartItem(
                                label = subjectName,
                                percentage = percentage
                            )
                        }
                    )
                }
            }

            // Section 4: Progress Trend (Line Chart) - REAL DATA
            Column(
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Text(
                    "7-Day Trend",
                    style = MaterialTheme.typography.titleLarge.copy(
                        fontWeight = FontWeight.Bold
                    )
                )

                // REAL DATA from review history with actual dates
                // Gets mastery % for each day of last 7 days from review sessions
                val (trendData, trendLabels) = remember(viewModel) {
                    viewModel?.get7DayMasteryTrend() 
                        ?: Pair(
                            listOf(0f, 0f, 0f, 0f, 0f, 0f, 0f),
                            listOf("Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun")
                        )
                }

                LineGraphChart(
                    dataPoints = trendData,
                    labels = trendLabels,
                    height = 240f  // Larger graph for better detail
                )
                
                // Show stats summary
                if (trendData.isNotEmpty()) {
                    val avgMastery = trendData.filter { it > 0f }.let { validPoints ->
                        if (validPoints.isNotEmpty()) validPoints.average() else 0.0
                    }
                    val maxMastery = trendData.maxOrNull() ?: 0f
                    val activeDays = trendData.count { it > 0f }
                    
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(12.dp),
                        horizontalArrangement = Arrangement.SpaceEvenly
                    ) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(
                                "Avg",
                                style = MaterialTheme.typography.labelSmall,
                                color = FlashcardColors.getQuestionTextColor().copy(alpha = 0.6f)
                            )
                            Text(
                                "${avgMastery.toInt()}%",
                                style = MaterialTheme.typography.headlineSmall.copy(
                                    fontWeight = FontWeight.Bold,
                                    color = FlashcardColors.getMasteryColor("MASTERED")
                                )
                            )
                        }
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(
                                "Peak",
                                style = MaterialTheme.typography.labelSmall,
                                color = FlashcardColors.getQuestionTextColor().copy(alpha = 0.6f)
                            )
                            Text(
                                "${maxMastery.toInt()}%",
                                style = MaterialTheme.typography.headlineSmall.copy(
                                    fontWeight = FontWeight.Bold,
                                    color = FlashcardColors.getCorrectAnswerColor()
                                )
                            )
                        }
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(
                                "Active Days",
                                style = MaterialTheme.typography.labelSmall,
                                color = FlashcardColors.getQuestionTextColor().copy(alpha = 0.6f)
                            )
                            Text(
                                "$activeDays/7",
                                style = MaterialTheme.typography.headlineSmall.copy(
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.primary
                                )
                            )
                        }
                    }
                }
                
                Text(
                    "📊 Shows your daily mastery percentage. Higher = better! Empty days = no reviews.",
                    style = MaterialTheme.typography.labelSmall,
                    color = FlashcardColors.getQuestionTextColor().copy(alpha = 0.6f)
                )
            }

            // Section 5: Detailed Subject Breakdown
            if (subjectStats.isNotEmpty()) {
                Column(
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        "Subject Details",
                        style = MaterialTheme.typography.titleLarge.copy(
                            fontWeight = FontWeight.Bold
                        )
                    )

                    subjectStats.forEach { (subjectName, subjectStats) ->
                        SubjectDetailCard(
                            subjectName = subjectName,
                            stats = subjectStats
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(16.dp))
    }
}

@Composable
fun SubjectDetailCard(
    subjectName: String,
    stats: SubjectStats
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.2f)
        ),
        shape = androidx.compose.foundation.shape.RoundedCornerShape(12.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Text(
                subjectName,
                style = MaterialTheme.typography.titleMedium.copy(
                    fontWeight = FontWeight.SemiBold
                )
            )

            val progressPercentage = if (stats.totalCards > 0) {
                (stats.masteredCards.toFloat() / stats.totalCards)
            } else {
                0f
            }

            Column(
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        "Progress",
                        style = MaterialTheme.typography.labelMedium
                    )
                    Text(
                        "${(progressPercentage * 100).toInt()}%",
                        style = MaterialTheme.typography.labelMedium.copy(
                            fontWeight = FontWeight.Bold
                        )
                    )
                }

                LinearProgressIndicator(
                    progress = { progressPercentage },
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(8.dp),
                    color = FlashcardColors.getMasteryColor(
                        when {
                            progressPercentage >= 0.75f -> "MASTERED"
                            progressPercentage >= 0.5f -> "REVIEW"
                            else -> "NEW"
                        }
                    ),
                    trackColor = MaterialTheme.colorScheme.surfaceVariant
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Column {
                    Text(
                        "${stats.masteredCards}",
                        style = MaterialTheme.typography.labelLarge.copy(
                            fontWeight = FontWeight.Bold,
                            color = FlashcardColors.getCorrectAnswerColor()
                        )
                    )
                    Text(
                        "Mastered",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                    )
                }

                Column {
                    Text(
                        "${stats.totalCards}",
                        style = MaterialTheme.typography.labelLarge.copy(
                            fontWeight = FontWeight.Bold
                        )
                    )
                    Text(
                        "Total",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                    )
                }

                if (stats.dueForReview > 0) {
                    Column {
                        Text(
                            "${stats.dueForReview}",
                            style = MaterialTheme.typography.labelLarge.copy(
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.error
                            )
                        )
                        Text(
                            "Due",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                        )
                    }
                }
            }
        }
    }
}
